/**
 * CommunityCardsView for Poker Reborn
 *
 * Displays the community cards (flop, turn, river) in the center of the table.
 * Uses CardView components for rendering with flip animation support.
 *
 * @see docs/screens/room/COMMUNITY_CARDS.md - Board layout, deal animations
 * @see docs/screens/room/CARD_VIEW.md - CardView component reference
 * @see docs/systems/STREET_END_ANIMATION.md - Animation sequence (FLOP-4, TURN-4, RIVER-4)
 */

import { Container, Graphics } from 'pixi.js';
import { COMMUNITY_CARDS } from '@/config/layout';
import { CardView, CARD_STYLES } from '@/components/cards/CardView';
import { DrawPredictionOverlay } from '@/components/table/DrawPredictionOverlay';
import type { Card } from '@/types/game';
import type { DrawInfo } from '@/utils/drawDetection';
import { getScaledValue } from '@/utils/responsive';
import { addOriginMarker, ORIGIN_COLORS } from '@/utils/OriginDebug';

// =============================================================================
// STYLES
// =============================================================================

/** Winning card highlight styles */
const HIGHLIGHT_STYLES = {
  // Strong highlight (core cards) - gold
  glowColor: 0xffd700,
  glowColorAlt: 0xffaa00, // Orange-gold for pulse
  glowWidth: 6,
  glowExpand: 8,
  liftAmount: 15,
  // Subtle highlight (kicker cards) - gray
  glowColorSubtle: 0xd3d3d3, // Light gray
  glowWidthSubtle: 3,
  glowExpandSubtle: 4,
  liftAmountSubtle: 0, // No lift for kickers
  // Animation timing
  animationDuration: 600, // ms for initial animation
  pulseSpeed: 1500, // ms per pulse cycle
};

/** Card spacing */
const CARD_SPACING = 10;

/**
 * Community cards (flop/turn/river) animation timing.
 * Controls how cards slide down onto the table.
 *
 * @see docs/systems/SOUND_EVENTS.md - Sound synchronization
 */
const SLIDE_ANIMATION = {
  startY: -300, // Start position (pixels above table)
  duration: 250, // Animation duration per card (ms)
  stagger: 420, // Delay between flop cards (ms)
};

// =============================================================================
// BREAKPOINT-BASED SCALING (BBS)
// =============================================================================

/**
 * BBS: Card width at specific screen widths.
 * Add/remove entries as needed - interpolation is automatic.
 *
 * @see docs/systems/BBS.md - Unified responsive system
 */
const COMMUNITY_CARD_WIDTH_BREAKPOINTS: Record<number, number> = {
  1920: 120,
  1440: 90,
  720: 50,
  375: 42,
};

export class CommunityCardsView extends Container {
  private cardViews: CardView[] = [];
  private cards: Card[] = [];
  private glowGraphics: Map<number, Graphics> = new Map();
  private highlightLevels: Map<number, 'strong' | 'subtle'> = new Map();
  private highlightAnimationId: number | null = null;
  private highlightStartTime = 0;
  private currentScale = 1; // Track current layout scale
  private isAnimating = false; // T36: Guard against refresh() during animation

  // T71: Draw prediction overlays on turn/river slots
  private turnOverlay: DrawPredictionOverlay;
  private riverOverlay: DrawPredictionOverlay;
  private pendingTurnDraws: DrawInfo[] | null = null;
  private pendingRiverDraws: DrawInfo[] | null = null;

  constructor() {
    super();

    // Create 5 CardView instances
    for (let i = 0; i < 5; i++) {
      const cardView = new CardView('large');
      const pos = COMMUNITY_CARDS.positions[i];
      if (pos) {
        // Position relative to center, convert from normalized to pixels
        cardView.x = pos.x * 1920; // Will be adjusted on resize
      }
      cardView.visible = false;
      this.addChild(cardView);
      this.cardViews.push(cardView);
    }

    // T71: Create draw prediction overlays for turn and river slots
    this.turnOverlay = new DrawPredictionOverlay();
    this.addChild(this.turnOverlay);
    this.riverOverlay = new DrawPredictionOverlay();
    this.addChild(this.riverOverlay);

    // Debug: Add origin marker if ?showOrigins=true
    addOriginMarker(this, ORIGIN_COLORS.community);
  }

  // ===========================================================================
  // PUBLIC API - Immediate Display (backwards compatible)
  // ===========================================================================

  /**
   * Set all community cards (immediate display, face-up)
   */
  setCards(cards: Card[]): void {
    console.log('[CommunityCardsView] setCards:', cards.length, 'cards:', cards.map(c => `${c.rank}${c.suit}`));
    this.cards = cards;
    this.updateDisplay(true);
  }

  /**
   * Deal flop (3 cards) - immediate display, face-up
   * For animated version, use dealFlopFaceDown() + flipCardsAnimated()
   */
  dealFlop(cards: [Card, Card, Card]): void {
    console.log('[CommunityCardsView] dealFlop:', cards.map(c => `${c.rank}${c.suit}`));
    this.cards = [...cards];
    this.updateDisplay(true);
  }

  /**
   * Deal turn (4th card) - immediate display, face-up
   * For animated version, use dealTurnFaceDown() + flipCardsAnimated()
   */
  dealTurn(card: Card): void {
    console.log('[CommunityCardsView] dealTurn:', `${card.rank}${card.suit}`, 'current cards:', this.cards.length);
    if (this.cards.length >= 3) {
      this.cards = [...this.cards.slice(0, 3), card];
      this.updateSingleCard(3, card, true);
    }
  }

  /**
   * Deal river (5th card) - immediate display, face-up
   * For animated version, use dealRiverFaceDown() + flipCardsAnimated()
   */
  dealRiver(card: Card): void {
    console.log('[CommunityCardsView] dealRiver:', `${card.rank}${card.suit}`, 'current cards:', this.cards.length);
    if (this.cards.length >= 4) {
      this.cards = [...this.cards.slice(0, 4), card];
      this.updateSingleCard(4, card, true);
    }
  }

  // ===========================================================================
  // PUBLIC API - Prepare Cards (called by state subscription)
  // ===========================================================================

  /**
   * Prepare flop cards for animation (3 cards).
   * Sets up cards at start position but does NOT start animation.
   * Call flipCardsAnimated('flop') to trigger the slide-down animation.
   */
  prepareFlopCards(cards: [Card, Card, Card]): void {
    console.log('[CommunityCardsView] prepareFlopCards:', cards.map(c => `${c.rank}${c.suit}`));
    this.cards = [...cards];

    // T36: If tab is hidden, skip animation prep - show cards immediately
    // Animations won't be visible anyway, and this prevents glitches on tab return
    if (document.hidden) {
      console.log('[CommunityCardsView] prepareFlopCards: tab hidden, showing immediately');
      this.updateDisplay(true);
      return;
    }

    // T36: Set animation flag when prepared (not just during animation)
    // This prevents refresh() from interfering between prepare and animate
    this.isAnimating = true;

    // Set up all 3 cards face-up at start position (hidden until animation starts)
    for (let i = 0; i < 3; i++) {
      const cardView = this.cardViews[i];
      const card = cards[i];
      if (cardView && card) {
        cardView.visible = false; // Hidden until animation starts
        cardView.setCard(card);
        cardView.flip(true, false); // Face-up
        cardView.y = SLIDE_ANIMATION.startY;
      }
    }
  }

  /**
   * Prepare turn card for animation (4th card).
   * Sets up card at start position but does NOT start animation.
   * Call flipCardsAnimated('turn') to trigger the slide-down animation.
   */
  prepareTurnCard(card: Card): void {
    console.log('[CommunityCardsView] prepareTurnCard:', `${card.rank}${card.suit}`);
    if (this.cards.length >= 3) {
      this.cards = [...this.cards.slice(0, 3), card];

      // T36: If tab is hidden, skip animation prep - show cards immediately
      if (document.hidden) {
        console.log('[CommunityCardsView] prepareTurnCard: tab hidden, showing immediately');
        this.updateDisplay(true);
        return;
      }

      // T36: Set animation flag when prepared
      this.isAnimating = true;

      const cardView = this.cardViews[3];
      if (cardView) {
        cardView.visible = false; // Hidden until animation starts
        cardView.setCard(card);
        cardView.flip(true, false); // Face-up
        cardView.y = SLIDE_ANIMATION.startY;
      }
    }
  }

  /**
   * Prepare river card for animation (5th card).
   * Sets up card at start position but does NOT start animation.
   * Call flipCardsAnimated('river') to trigger the slide-down animation.
   */
  prepareRiverCard(card: Card): void {
    console.log('[CommunityCardsView] prepareRiverCard:', `${card.rank}${card.suit}`);
    if (this.cards.length >= 4) {
      this.cards = [...this.cards.slice(0, 4), card];

      // T36: If tab is hidden, skip animation prep - show cards immediately
      if (document.hidden) {
        console.log('[CommunityCardsView] prepareRiverCard: tab hidden, showing immediately');
        this.updateDisplay(true);
        return;
      }

      // T36: Set animation flag when prepared
      this.isAnimating = true;

      const cardView = this.cardViews[4];
      if (cardView) {
        cardView.visible = false; // Hidden until animation starts
        cardView.setCard(card);
        cardView.flip(true, false); // Face-up
        cardView.y = SLIDE_ANIMATION.startY;
      }
    }
  }

  // ===========================================================================
  // PUBLIC API - Animated Deal (called by StreetEndSequencer)
  // ===========================================================================

  /**
   * Start slide-down animation for prepared cards and wait for completion.
   * Cards must be prepared first via prepareFlopCards/prepareTurnCard/prepareRiverCard.
   *
   * This is called by StreetEndSequencer at the right time in the sequence
   * (after chip collection animation completes).
   *
   * @param street - 'flop' | 'turn' | 'river'
   * @returns Promise that resolves when animation complete
   */
  async flipCardsAnimated(street: 'flop' | 'turn' | 'river'): Promise<void> {
    console.log(`[CommunityCardsView] flipCardsAnimated: starting ${street} animation`);

    // T36: If cards were already shown (tab was hidden during prepare), skip animation
    // Check if first relevant card is already at y=0 (final position)
    const checkIndex = street === 'flop' ? 0 : street === 'turn' ? 3 : 4;
    const checkCard = this.cardViews[checkIndex];
    if (checkCard && checkCard.visible && checkCard.y === 0) {
      console.log(`[CommunityCardsView] flipCardsAnimated: ${street} cards already visible, skipping`);
      return;
    }

    // T36: Set animation flag to prevent refresh() interference
    this.isAnimating = true;

    try {
      if (street === 'flop') {
        // Make cards visible and animate with stagger
        for (let i = 0; i < 3; i++) {
          const cardView = this.cardViews[i];
          if (cardView) {
            cardView.visible = true;
            const delay = i * SLIDE_ANIMATION.stagger;
            this.animateSlideDown(cardView, delay);
          }
        }
        // Wait for all 3 cards to complete: (2 * stagger) + duration
        const totalDuration = (2 * SLIDE_ANIMATION.stagger) + SLIDE_ANIMATION.duration;
        await new Promise(resolve => setTimeout(resolve, totalDuration));
      } else if (street === 'turn') {
        // Make turn card visible and animate
        const cardView = this.cardViews[3];
        if (cardView) {
          cardView.visible = true;
          this.animateSlideDown(cardView, 0);
        }
        await new Promise(resolve => setTimeout(resolve, SLIDE_ANIMATION.duration));
      } else if (street === 'river') {
        // Make river card visible and animate
        const cardView = this.cardViews[4];
        if (cardView) {
          cardView.visible = true;
          this.animateSlideDown(cardView, 0);
        }
        await new Promise(resolve => setTimeout(resolve, SLIDE_ANIMATION.duration));
      }

      console.log(`[CommunityCardsView] flipCardsAnimated complete: ${street}`);
    } finally {
      this.isAnimating = false;

      // T71: Apply any pending draw predictions that arrived during animation
      if (this.pendingTurnDraws) {
        this.turnOverlay.setDraws(this.pendingTurnDraws);
        this.pendingTurnDraws = null;
      }
      if (this.pendingRiverDraws) {
        this.riverOverlay.setDraws(this.pendingRiverDraws);
        this.pendingRiverDraws = null;
      }

      // T71: Hide overlay for the slot that just received a card
      if (street === 'turn') {
        this.turnOverlay.clear();
      } else if (street === 'river') {
        this.riverOverlay.clear();
      }
    }
  }

  // ===========================================================================
  // PUBLIC API - Other
  // ===========================================================================

  /**
   * Check if a card animation is currently in progress.
   * Used by TableScreen.refresh() to avoid interfering with ongoing animations.
   * T36: Prevents race condition when tab becomes visible during animation.
   */
  isAnimationInProgress(): boolean {
    return this.isAnimating;
  }

  /**
   * Clear all cards
   */
  clear(): void {
    console.log('[CommunityCardsView] clear: resetting all cards');
    this.cards = [];
    this.cardViews.forEach((cardView) => {
      cardView.visible = false;
      cardView.setCard(null);
    });
    this.resetHighlights();
    this.clearDrawPredictions();
  }

  /**
   * Highlight winning cards with animated glow effect.
   * Core cards get strong gold highlight with lift and pulse.
   * Kicker cards get subtle gray highlight without lift or pulse.
   */
  highlightCards(coreCards: Card[], kickerCards: Card[] = []): void {
    console.log(
      '[CommunityCardsView] highlightCards:',
      `core=${coreCards.map((c) => `${c.rank}${c.suit}`).join(',')}`,
      `kicker=${kickerCards.map((c) => `${c.rank}${c.suit}`).join(',')}`
    );
    // Stop any existing animation
    this.stopHighlightAnimation();
    this.highlightLevels.clear();

    // Track which cards are highlighted
    const highlightIndices: number[] = [];

    // Compare cards and set up highlights
    this.cards.forEach((card, index) => {
      const cardView = this.cardViews[index];
      if (!cardView) return;

      // Check if this community card is a core card (strong highlight)
      const isCore = coreCards.some(
        (wc) => wc.rank === card.rank && wc.suit === card.suit
      );

      // Check if this community card is a kicker card (subtle highlight)
      const isKicker = kickerCards.some(
        (wc) => wc.rank === card.rank && wc.suit === card.suit
      );

      if (isCore) {
        highlightIndices.push(index);
        this.highlightLevels.set(index, 'strong');
        this.createGlowGraphics(index, cardView, 'strong');
      } else if (isKicker) {
        highlightIndices.push(index);
        this.highlightLevels.set(index, 'subtle');
        this.createGlowGraphics(index, cardView, 'subtle');
      } else {
        // Dim non-winning cards
        cardView.darken(true);
      }
    });

    // Start animation loop
    if (highlightIndices.length > 0) {
      this.highlightStartTime = performance.now();
      this.animateHighlights(highlightIndices);
    }
  }

  /**
   * Reset card highlights
   */
  resetHighlights(): void {
    console.log('[CommunityCardsView] resetHighlights');
    // Stop animation
    this.stopHighlightAnimation();

    // Clear highlight level tracking
    this.highlightLevels.clear();

    // Remove glow graphics and reset card views
    this.cardViews.forEach((cardView, index) => {
      cardView.y = 0;
      cardView.darken(false);
      // Restore layout scale (not reset to 1)
      cardView.scale.set(this.currentScale);

      const glow = this.glowGraphics.get(index);
      if (glow) {
        cardView.removeChild(glow);
        glow.destroy();
        this.glowGraphics.delete(index);
      }
    });
  }

  /**
   * Nuclear reset - restore ALL card properties to pristine state.
   * Called at hand start to ensure clean slate.
   */
  resetAllCards(): void {
    console.log('[CommunityCardsView] resetAllCards: nuclear reset');

    // Stop any highlight animations
    this.stopHighlightAnimation();

    // Clear cards array and draw overlays
    this.cards = [];
    this.clearDrawPredictions();

    // Nuclear reset on each card
    this.cardViews.forEach((cardView, index) => {
      // Remove any glow graphics
      const glow = this.glowGraphics.get(index);
      if (glow) {
        cardView.removeChild(glow);
        glow.destroy();
        this.glowGraphics.delete(index);
      }

      // Nuclear reset on the card itself
      cardView.resetAll();

      // Restore layout-specific transforms
      cardView.scale.set(this.currentScale);
      cardView.y = 0;
      cardView.visible = false;
    });
  }

  // ===========================================================================
  // PUBLIC API - Draw Prediction Overlays (T71)
  // ===========================================================================

  /**
   * Show draw prediction overlays on empty turn/river card slots.
   * If animation is in progress, stores draws as pending.
   */
  showDrawPredictions(draws: DrawInfo[], street: 'flop' | 'turn'): void {
    if (street === 'flop') {
      // On flop: show overlays on both turn and river slots
      if (this.isAnimating) {
        this.pendingTurnDraws = draws;
        this.pendingRiverDraws = draws;
      } else {
        this.turnOverlay.setDraws(draws);
        this.riverOverlay.setDraws(draws);
      }
    } else if (street === 'turn') {
      // On turn: only river slot gets overlay (turn card is already dealt)
      this.turnOverlay.clear();
      if (this.isAnimating) {
        this.pendingRiverDraws = draws;
      } else {
        this.riverOverlay.setDraws(draws);
      }
    }
  }

  /**
   * Clear all draw prediction overlays.
   */
  clearDrawPredictions(): void {
    this.turnOverlay.clear();
    this.riverOverlay.clear();
    this.pendingTurnDraws = null;
    this.pendingRiverDraws = null;
  }

  /**
   * Update layout for screen size
   */
  updateLayout(screenWidth: number): void {
    const cardWidth = getScaledValue(screenWidth, COMMUNITY_CARD_WIDTH_BREAKPOINTS, { discrete: false });
    const scale = cardWidth / CARD_STYLES['large'].width;
    this.currentScale = scale;

    const cardDims = CARD_STYLES['large'];
    const spacing = (cardDims.width + CARD_SPACING) * scale;

    this.cardViews.forEach((cardView, index) => {
      cardView.x = (index - 2) * spacing;
      cardView.scale.set(scale);
    });

    // T71: Position overlays at turn (index 3) and river (index 4) card slots
    this.turnOverlay.x = (3 - 2) * spacing;
    this.turnOverlay.y = 0;
    this.turnOverlay.scale.set(scale);
    this.riverOverlay.x = (4 - 2) * spacing;
    this.riverOverlay.y = 0;
    this.riverOverlay.scale.set(scale);
  }

  // ===========================================================================
  // PRIVATE METHODS - Display
  // ===========================================================================

  /**
   * Update display for all cards
   * @param faceUp - Whether to show cards face-up
   */
  private updateDisplay(faceUp: boolean): void {
    this.cardViews.forEach((cardView, index) => {
      const card = this.cards[index];

      if (card) {
        // T36: Reset position and alpha (defense against race conditions)
        cardView.y = 0;
        cardView.alpha = 1;
        cardView.visible = true;
        cardView.setCard(card);
        // Set face state immediately (no animation)
        cardView.flip(faceUp, false);
      } else {
        cardView.visible = false;
      }
    });
  }

  /**
   * Update a single card
   * @param index - Card index (0-4)
   * @param card - Card data
   * @param faceUp - Whether to show face-up
   */
  private updateSingleCard(index: number, card: Card, faceUp: boolean): void {
    const cardView = this.cardViews[index];
    if (cardView) {
      cardView.visible = true;
      cardView.setCard(card);
      cardView.flip(faceUp, false);
    }
  }

  /**
   * Animate a card sliding down from startY to 0 with fade-in
   */
  private animateSlideDown(cardView: CardView, delay: number): void {
    const startY = SLIDE_ANIMATION.startY;
    const duration = SLIDE_ANIMATION.duration;
    let startTime: number | null = null;

    // Start transparent
    cardView.alpha = 0;

    const animate = (timestamp: number) => {
      if (startTime === null) {
        startTime = timestamp + delay;
      }

      const elapsed = timestamp - startTime;

      if (elapsed < 0) {
        // Still in delay period
        requestAnimationFrame(animate);
        return;
      }

      const progress = Math.min(elapsed / duration, 1);
      const eased = this.easeOutCubic(progress);

      // Interpolate from startY to 0
      cardView.y = startY * (1 - eased);

      // Fade from transparent to opaque
      cardView.alpha = eased;

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  // ===========================================================================
  // PRIVATE METHODS - Highlight Animation
  // ===========================================================================

  /**
   * Create glow graphics for a winning card
   * @param level - 'strong' for gold (core cards), 'subtle' for gray (kicker cards)
   */
  private createGlowGraphics(
    index: number,
    cardView: CardView,
    level: 'strong' | 'subtle' = 'strong'
  ): void {
    // Remove existing glow if any
    const existingGlow = this.glowGraphics.get(index);
    if (existingGlow) {
      cardView.removeChild(existingGlow);
      existingGlow.destroy();
    }

    const cardDims = CARD_STYLES['large'];
    const glow = new Graphics();
    const isStrong = level === 'strong';

    // Get style values based on level
    const glowColor = isStrong
      ? HIGHLIGHT_STYLES.glowColor
      : HIGHLIGHT_STYLES.glowColorSubtle;
    const glowExpand = isStrong
      ? HIGHLIGHT_STYLES.glowExpand
      : HIGHLIGHT_STYLES.glowExpandSubtle;
    const glowWidth = isStrong
      ? HIGHLIGHT_STYLES.glowWidth
      : HIGHLIGHT_STYLES.glowWidthSubtle;
    const outerAlpha = isStrong ? 0.3 : 0.15;

    // Draw outer glow (larger, more transparent)
    glow.roundRect(
      -cardDims.width / 2 - glowExpand,
      -cardDims.height / 2 - glowExpand,
      cardDims.width + glowExpand * 2,
      cardDims.height + glowExpand * 2,
      cardDims.cornerRadius + 4
    );
    glow.fill({ color: glowColor, alpha: outerAlpha });

    // Draw inner glow border
    glow.roundRect(
      -cardDims.width / 2 - 4,
      -cardDims.height / 2 - 4,
      cardDims.width + 8,
      cardDims.height + 8,
      cardDims.cornerRadius + 2
    );
    glow.setStrokeStyle({
      width: glowWidth,
      color: glowColor,
    });
    glow.stroke();

    glow.alpha = 0; // Start invisible
    cardView.addChildAt(glow, 0);
    this.glowGraphics.set(index, glow);
  }

  /**
   * Animate highlight effects.
   * Strong cards get lift and pulse, subtle cards get static glow only.
   */
  private animateHighlights(highlightIndices: number[]): void {
    const animate = () => {
      const elapsed = performance.now() - this.highlightStartTime;

      highlightIndices.forEach((index) => {
        const cardView = this.cardViews[index];
        const glow = this.glowGraphics.get(index);
        if (!cardView || !glow) return;

        const level = this.highlightLevels.get(index) ?? 'strong';
        const isStrong = level === 'strong';
        const liftAmount = isStrong
          ? HIGHLIGHT_STYLES.liftAmount
          : HIGHLIGHT_STYLES.liftAmountSubtle;

        if (elapsed < HIGHLIGHT_STYLES.animationDuration) {
          // Initial fade-in and lift animation
          const progress = elapsed / HIGHLIGHT_STYLES.animationDuration;
          const eased = this.easeOutCubic(progress);

          glow.alpha = eased;
          cardView.y = -liftAmount * eased;
        } else if (isStrong) {
          // Pulse animation for strong cards only
          const pulseElapsed = elapsed - HIGHLIGHT_STYLES.animationDuration;
          const pulseProgress =
            (pulseElapsed % HIGHLIGHT_STYLES.pulseSpeed) / HIGHLIGHT_STYLES.pulseSpeed;

          // Sine wave for smooth pulsing (0.7 to 1.0 range)
          const pulseValue = 0.7 + 0.3 * Math.sin(pulseProgress * Math.PI * 2);
          glow.alpha = pulseValue;

          // Slight scale pulse (based on current layout scale)
          const pulseMultiplier = 1 + 0.02 * Math.sin(pulseProgress * Math.PI * 2);
          const scaleValue = this.currentScale * pulseMultiplier;
          cardView.scale.set(scaleValue);
        } else {
          // Subtle cards stay static after fade-in
          glow.alpha = 0.6; // Lower steady alpha for subtle
        }
      });

      this.highlightAnimationId = requestAnimationFrame(animate);
    };

    this.highlightAnimationId = requestAnimationFrame(animate);
  }

  /**
   * Stop highlight animation
   */
  private stopHighlightAnimation(): void {
    if (this.highlightAnimationId !== null) {
      cancelAnimationFrame(this.highlightAnimationId);
      this.highlightAnimationId = null;
    }
  }

  /**
   * Easing function for smooth animation
   */
  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }
}
